import React from 'react';
import { css, resolveStyle, StyleSheet } from './style_sheet';
import { View, ScrollView } from './views';
import { KeyboardAvoidingView, Keyboard } from './keyboard_avoiding_view';
import { FlatList } from './flat_list';
import { Linking } from './linking';
import { Dimensions, useWindowDimensions } from './dimensions';

export { css, resolveStyle, StyleSheet } from './style_sheet';
export { View, ScrollView } from './views';
export { KeyboardAvoidingView, Keyboard } from './keyboard_avoiding_view';
export { FlatList } from './flat_list';
export { Linking } from './linking';
export { Dimensions, useWindowDimensions } from './dimensions';

export class TouchableWithoutFeedback extends React.PureComponent<any> {
  render() {
    const {
      style,
      className: extraClass,
      children,
      onPress,
      ...other_props
    } = this.props;

    const { className, inlineStyle } = resolveStyle(style, extraClass);

    return (
      <div
        onClick={onPress}
        className={className + ' touchable-without-feedback'}
        style={inlineStyle}
        {...other_props}
      >
        {children}
      </div>
    );
  }
}

function noop() {}
const DEFAULT_DELAY = 500;

export class TouchableHighlight extends React.PureComponent<any> {
  static defaultProps = {
    underlayColor: 'rgba(0,0,0,0.5)',
  };
  _timeout: any = null;
  _sentLongPress = false;
  componentWillUnmount() {
    this._clear();
  }
  _start = () => {
    if (!this._timeout) {
      this._timeout = setTimeout(
        this._onLongPress,
        this.props.delay ?? DEFAULT_DELAY
      );
    }
  };
  _clear = () => {
    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = null;
    }
  };
  _onLongPress = () => {
    this._sentLongPress = true;
    this.props.onLongPress();
  };
  _onPress = () => {
    if (this._sentLongPress) {
      this._sentLongPress = false;
    } else {
      this.props.onPress?.();
    }
  };

  render() {
    const {
      style,
      children,
      underlayColor,
      pointerEvents: _,
      onPress,
      onLongPress,
      ...other_props
    } = this.props;

    const { className, inlineStyle } = resolveStyle(style);
    const inner_style = { backgroundColor: underlayColor };
    const extra = onLongPress
      ? {
          onMouseDown: this._start,
          onTouchStart: this._start,
          onMouseUp: this._clear,
          onMouseLeave: this._clear,
          onTouchEnd: this._clear,
          onClick: this._onPress,
        }
      : {
          onTouchStart: noop,
          onClick: onPress,
        };
    return (
      <div
        className={className + ' touchable-highlight'}
        style={inlineStyle}
        {...extra}
        {...other_props}
      >
        <div className='touchable-highlight-underlay' style={inner_style} />
        {children}
      </div>
    );
  }
}
export class Text extends React.PureComponent<any> {
  render() {
    const {
      style,
      className: extraClass,
      children,
      pointerEvents: _,
      ...other_props
    } = this.props;

    const { className, inlineStyle } = resolveStyle(style, extraClass);
    return (
      <div className={className} style={inlineStyle} {...other_props}>
        {children}
      </div>
    );
  }
}
export function BaseInput(props: any) {
  const { style, className: extraClass, ...other_props } = props;

  const { className, inlineStyle } = resolveStyle(style, extraClass);
  const input_props: any = {
    className: `base-component-text-input ${className}`,
    style: inlineStyle,
    ...other_props,
  };
  if (other_props.type === 'checkbox') {
    input_props.checked = props.value;
    input_props.className += ' base-component-checkbox-input';
  } else if (other_props.type === 'radio') {
    input_props.className += ' base-component-radio-input';
  }

  return <input {...input_props} />;
}
export function TextInput(props: any) {
  const {
    style,
    className: extraClass,
    onSubmitEditing,
    secureTextEntry,
    onChangeText,
    multiline,
    autoGrow,
    autoCorrect,
    keyboardType,
    returnKeyType,
    placeholderTextColor,
    ...other_props
  } = props;
  function _onChange(e: any) {
    onChangeText?.(e?.target?.value);
  }

  if (secureTextEntry) {
    other_props.type = 'password';
  }
  if (returnKeyType) {
    other_props.enterKeyHint = returnKeyType;
  }
  if (autoCorrect) {
    other_props.autoCorrect = 'on';
  }

  let { className, inlineStyle } = resolveStyle(style, extraClass);
  if (other_props.type === 'checkbox') {
    className += ' base-component-checkbox-input';
  }
  const input_props: any = {
    className: 'base-component-text-input ' + className,
    style: inlineStyle,
    onChange: _onChange,
    onInput: _onChange,
    ...other_props,
  };

  const placeholderClass = React.useMemo(() => {
    let ret = '';
    if (placeholderTextColor) {
      const sheet = StyleSheet.create({
        input: {
          '::placeholder': {
            color: placeholderTextColor,
          },
        },
      });
      ret = css(sheet.input);
    }
    return ret;
  }, [placeholderTextColor]);
  if (placeholderClass) {
    input_props.className += ' ' + placeholderClass;
  }

  let input;
  if (multiline) {
    if (autoGrow) {
      input = (
        <div
          className={'base-component-text-input-autogrow ' + className}
          style={inlineStyle}
        >
          <textarea
            className={
              'base-component-text-input-autogrow-textarea ' + placeholderClass
            }
            value={input_props.value}
            onChange={_onChange}
            onInput={_onChange}
            rows={props.rows || 1}
            {...other_props}
          />
          <div className='base-component-text-input-autogrow-after'>
            {props.value + ' '}
          </div>
        </div>
      );
    } else {
      input = <textarea {...input_props} rows={props.rows || 1} />;
    }
  } else {
    input = <input {...input_props} />;
  }

  function _onSubmitEditing(e: any) {
    e?.preventDefault?.();
    onSubmitEditing(e);
    return false;
  }
  let content;
  if (onSubmitEditing) {
    content = (
      <form
        className='base-component-text-input-form'
        onSubmit={_onSubmitEditing}
      >
        {input}
      </form>
    );
  } else {
    content = input;
  }
  return content;
}

export class Image extends React.PureComponent<any> {
  static defaultProps = {
    resizeMode: 'contain',
  };

  render() {
    const {
      style,
      resizeMode,
      source,
      getDiv,
      className: extraClass,
    } = this.props;

    const { className, inlineStyle } = resolveStyle(style, extraClass);

    if (resizeMode === 'tile') {
      inlineStyle.backgroundRepeat = 'repeat';
    } else {
      inlineStyle.backgroundSize = resizeMode;
      inlineStyle.backgroundRepeat = 'no-repeat';
      inlineStyle.backgroundPosition = 'center center';
    }
    const uri = (source && (source.src || source.uri)) || source;
    inlineStyle.backgroundImage = "url('" + uri + "')";
    const extra_props: any = {};
    if (getDiv) {
      extra_props.ref = getDiv;
    }
    return <div className={className} style={inlineStyle} {...extra_props} />;
  }
}

export const PixelRatio = {
  get: () => window.devicePixelRatio || 1,
};

type StatusBarProps = {
  barStyle?: any;
  backgroundColor?: any;
  translucent?: any;
};
export class StatusBar extends React.PureComponent<StatusBarProps> {
  static setBarStyle() {}
  static setBackgroundColor() {}
  render() {
    return null;
  }
}

export function ActivityIndicator() {
  return null;
}
export function Animated() {}
export const AppState: any = false;
export const Easing: any = false;
export function rem(amount: number): number {
  return amount;
}

function alert(title: string, message?: string, buttons?: any) {
  if (buttons?.length > 1) {
    const result = window.confirm(message || title);
    const confirm_button = buttons.find((b: any) => b.confirm) || buttons[0];
    const other_button =
      buttons[0] === confirm_button ? buttons[1] : buttons[0];
    if (result) {
      confirm_button.onPress?.();
    } else {
      other_button.onPress?.();
    }
  } else {
    window.alert(message || title);
    buttons?.[0]?.onPress?.();
  }
}
function share(content: any, options?: any) {
  const { message, url, title } = content;
  return new Promise((resolve, reject) => {
    try {
      const opts = { url, title, text: message };
      const p = window?.navigator?.share?.(opts);
      if (p) {
        p.then(
          (result: any) => {
            resolve(result || {});
          },
          (err: any) => {
            if (err?.name === 'AbortError') {
              resolve({ action: 'dismissedAction', activityType: null });
            } else {
              reject(err);
            }
          }
        );
      } else {
        resolve({});
      }
    } catch (e) {
      reject(e);
    }
  });
}
export const Alert = { alert };
export const Share = { share, disabled: !window?.navigator?.share };

export default {
  Animated,
  Alert,
  ActivityIndicator,
  StyleSheet,
  View,
  ScrollView,
  Text,
  BaseInput,
  TextInput,
  Image,
  TouchableHighlight,
  TouchableWithoutFeedback,
  Dimensions,
  PixelRatio,
  StatusBar,
  Share,
  Keyboard,
  KeyboardAvoidingView,
  FlatList,
  Linking,
  css,
  rem,
  resolveStyle,
  useWindowDimensions,
};
